import type { ContractSeasonData } from 'gql/graphql';
import { type ContractChangeStatusEnum } from 'gql/graphql';

import { useImperativeHandle, type MutableRefObject } from 'react';

import { AgreementFormTopPanel } from '../AgreementFormTopPanel';
import { AgreementPriceListTable } from '../AgreementPriceListTable';
import { AgreementsAddOther } from '../AgreementsAddOther';
import { useGetAgreementOtherItems } from '../hooks/useGetAgreementOtherItems';
import { useRemoveAgreementsOther } from '../hooks/useRemoveAgreementsOther';

const noDataComp = () => 'Для отображения данных добавьте прочие затраты';

export const AgreementPriceListOther = ({
    agreementId,
    refreshData,
    disabledByStatus,
    infoText = undefined,
    needUpdateCurrentPriceList,
    needUpdatePriceListHandler,
    isDisabledByProjectStatus,
    contractChangeStatus,
    isDisabledByContractStatus,
    agreementTableApiRef,
    hasItemRow,
    contractId,
    contractSeasons,
}: {
    agreementId: number;
    refreshData: () => void;
    disabledByStatus: boolean;
    isDisabledByProjectStatus: boolean;
    isDisabledByContractStatus: boolean;
    infoText?: string;
    needUpdateCurrentPriceList?: boolean;
    contractChangeStatus?: ContractChangeStatusEnum;
    needUpdatePriceListHandler?: (nextState: boolean) => void;
    agreementTableApiRef: MutableRefObject<{ refreshCurrentTable: (skipCheckedReset?: boolean) => void } | undefined>;
    hasItemRow: boolean;
    contractId: number;
    contractSeasons: ContractSeasonData[];
}) => {
    const {
        datasource,
        columns,
        columnsForTableSettings,
        requestStatus,
        isColumnVisible,
        handleTableSettingsConfirm,
        rowCount,
        totalCount,
        refreshAgreementsOther,
        checkedIdsArr,
        handleClearChecked,
        refreshFilters,
        tableRef,
        contractConstructionTypes,
    } = useGetAgreementOtherItems({
        contractChangeId: agreementId,
        refreshData,
        disabledByStatus,
        isDisabledByProjectStatus,
        needUpdateCurrentPriceList,
        contractChangeStatus,
        needUpdatePriceListHandler,
        contractSeasons,
        contractId,
    });

    useImperativeHandle(agreementTableApiRef, () => ({ refreshCurrentTable: refreshAgreementsOther }), [
        refreshAgreementsOther,
    ]);

    const {
        removeOtherCostsData,
        removeOtherCostsStatus,
        removeOtherCostsError,
        removeOtherCostsSetData,
        handleRemoveOtherCosts,
    } = useRemoveAgreementsOther(agreementId, checkedIdsArr, refreshAgreementsOther, refreshFilters);

    return (
        <>
            <AgreementFormTopPanel infoText={infoText}>
                <AgreementsAddOther
                    disabledByStatus={disabledByStatus}
                    refreshAgreementsOther={refreshAgreementsOther}
                    isDisabledByProjectStatus={isDisabledByProjectStatus}
                    isDisabledByContractStatus={isDisabledByContractStatus}
                    refreshFilters={refreshFilters}
                    contractId={contractId}
                    agreementId={agreementId}
                    contractSeasons={contractSeasons}
                    contractConstructionTypes={contractConstructionTypes}
                />
            </AgreementFormTopPanel>
            <AgreementPriceListTable
                refreshCurrentTableRef={tableRef}
                rowCount={rowCount}
                totalCount={totalCount}
                columnsForTableSettings={columnsForTableSettings}
                handleTableSettingsConfirm={handleTableSettingsConfirm}
                isColumnVisible={isColumnVisible}
                datasource={datasource}
                columns={columns}
                requestStatus={requestStatus}
                checkedIdsArr={checkedIdsArr}
                handleClearChecked={handleClearChecked}
                removeRowsData={removeOtherCostsData}
                removeRowsStatus={removeOtherCostsStatus}
                removeRowsError={removeOtherCostsError}
                removeRowsSetData={removeOtherCostsSetData}
                handleRemoveRows={handleRemoveOtherCosts}
                tableContext={{ tableUniqueId: 'agreementOtherItems' }}
                emptyDataComponent={noDataComp}
                hasItemRow={hasItemRow}
                rowClassRules={{
                    groupRow: ({ data }) => (data as any)?.type === 'section',
                    defaultRowClass: ({ data }) => (data as any)?.type !== 'section',
                }}
            />
        </>
    );
};

AgreementPriceListOther.displayName = 'AgreementPriceListOther';
